import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useMemo } from 'react'

import ScreenLayout from '@/components/layout/ScreenLayout'
import DataTable, { type DataTableColumn } from '@/components/ui/DataTable'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import EmptyState from '@/components/ui/EmptyState'
import SymbolCell from '@/components/ui/SymbolCell'
import TabBar, { TabPanel, type TabBarOption } from '@/components/ui/TabBar'
import TitleRow from '@/components/ui/TitleRow'
import RefreshRegion from '@/components/ui/RefreshRegion'
import UpdatingNote from '@/components/ui/UpdatingNote'
import { useIndexSymbols, useTrendSignals } from '@/features/signals/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import type { TrendRow } from '@/schemas/signals'
import SessionDatePicker from '@/screens/signals/components/SessionDatePicker'
import { signalCompanyColumn } from '@/screens/signals/components/SignalCompanyColumn'
import SignalsEmpty from '@/screens/signals/components/SignalsEmpty'
import SignalsFootnote from '@/screens/signals/components/SignalsFootnote'
import SignalsLocked from '@/screens/signals/components/SignalsLocked'
import SignalsRouteError from '@/screens/signals/components/SignalsRouteError'
import SignalsSearchBox from '@/screens/signals/components/SignalsSearchBox'
import { PickerSkeleton } from '@/screens/signals/components/SignalsSkeletons'
import SignalsTableFrame from '@/screens/signals/components/SignalsTableFrame'
import {
  SearchSkeleton,
  FootnoteSkeleton,
  TabsSkeleton,
  TableFrameSkeleton,
  ToolbarSkeleton,
} from '@/screens/signals/components/SignalsTableSkeletons'
import {
  AveragesCell,
  ConditionSummary,
  ConditionsMet,
  DirectionCell,
  PriceCell,
} from '@/screens/signals/components/TrendConditionCells'
import { signalsRailProps } from '@/screens/signals/components/signals-rail'
import { useSearchBox } from '@/screens/signals/lib/search-box'
import { useShownValue } from '@/screens/signals/lib/shown-value'
import { isSignalsLocked, signalRowLink } from '@/screens/signals/lib/signals-ui'
import {
  bearishConditions,
  breakdownOf,
  conditionsOf,
} from '@/screens/signals/lib/trend-conditions'
import {
  loadTrendSignals,
  trendQueryOf,
  trendSignalsSearchSchema,
  type TrendSignalsSearch,
} from '@/screens/signals/TrendSignalsScreen.loader'
import { SIGNAL_DEFAULT_PAGE_SIZE, toSignalPageSize } from '@/screens/signals/search'

const TITLE = 'Trend Signals'

/** Ties the side tabs to the one panel under them. */
const TABS_ID = 'trend-signals'

const TAB_OPTIONS: readonly TabBarOption[] = [
  { value: 'positive', label: 'Positive Signals' },
  { value: 'negative', label: 'Negative Signals' },
]

type Tab = TrendSignalsSearch['tab']

/*
 * The loaded page, block for block: the tabs, search and date row, one panel
 * of fifteen two-line rows under its head band (five columns), the pager and
 * the footnote.
 */
function TrendSignalsPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading trend signals…"
      header={<TitleRow title={TITLE} />}
      {...signalsRailProps(TITLE)}
    >
      <TableFrameSkeleton
        rows={SIGNAL_DEFAULT_PAGE_SIZE}
        columns={5}
        toolbar={
          <ToolbarSkeleton
            start={<TabsSkeleton />}
            end={
              <>
                <SearchSkeleton />
                <PickerSkeleton />
              </>
            }
          />
        }
      />
      <FootnoteSkeleton className="h-8 @xl:h-4" />
    </ScreenLayout>
  )
}

function TrendSignalsError(props: ErrorComponentProps) {
  return (
    <SignalsRouteError
      {...props}
      header={<TitleRow title={TITLE} />}
      pageTitle={TITLE}
      title="Trend signals did not load"
    />
  )
}

export const Route = createFileRoute('/_app/market/signals/trend')({
  validateSearch: trendSignalsSearchSchema,
  loader: loadTrendSignals,
  pendingComponent: TrendSignalsPending,
  errorComponent: TrendSignalsError,
  component: TrendSignalsScreen,
})

/** The three condition columns' heads, by side: what the stock's price or averages are doing. */
const GROUP_HEADS = {
  positive: ['Price above', 'Averages rising', 'Direction'],
  negative: ['Price below', 'Averages falling', 'Direction'],
} as const

/**
 * The two tabs read one dataset under the same five columns: Company,
 * Conditions met (a bar and "5/13"), then the checks cut into Price above,
 * Averages rising and Direction (Negative: Price below, Averages falling,
 * −DI > +DI). Positive prints `met`, Negative `unmet` (already the bearish
 * wording; `bearishConditions` only turns the DI pair round), each with its
 * own count of the API's `total`, never a literal 13. The three detail columns
 * go from the table's `xl` rung (672px: five columns need it), and the same
 * words sit under the company name, so a narrow table loses no fact.
 * Memoised on the tab, so the cells keep their identity.
 */
const columnsFor = (tab: Tab): DataTableColumn<TrendRow>[] => {
  const positive = tab === 'positive'
  const listOf = (row: TrendRow) => (positive ? row.met : bearishConditions(row.unmet))
  const breakdown = (row: TrendRow) => breakdownOf(listOf(row), tab)
  const [priceHead, averagesHead, directionHead] = GROUP_HEADS[tab]
  const company = signalCompanyColumn<TrendRow>()
  return [
    {
      ...company,
      // Below the table's `xl` the three detail columns are gone and their words sit under the
      // company.
      cell: ({ row }) => (
        <div className="py-2">
          <SymbolCell
            symbol={row.original.stock.symbol}
            name={row.original.stock.name ?? ''}
            monogram={row.original.stock.symbol.slice(0, 2)}
            reserveMark
            {...(row.original.stock.logo === null ? {} : { logo: row.original.stock.logo })}
          />
          <ConditionSummary
            breakdown={breakdown(row.original)}
            labels={GROUP_HEADS[tab]}
            count={`${String(listOf(row.original).length)} of ${String(row.original.total)} met`}
            className="@2xl/table:hidden"
          />
        </div>
      ),
    },
    {
      id: 'count',
      accessorFn: (row) => listOf(row).length,
      header: 'Conditions met',
      enableSorting: false,
      meta: { hideBelow: 'sm' },
      cell: ({ row }) => (
        <ConditionsMet {...conditionsOf(row.original, tab)} total={row.original.total} side={tab} />
      ),
    },
    {
      id: 'price',
      accessorFn: (row) => breakdown(row).price.length,
      header: priceHead,
      enableSorting: false,
      meta: { hideBelow: 'xl' },
      cell: ({ row }) => <PriceCell breakdown={breakdown(row.original)} />,
    },
    {
      id: 'averages',
      accessorFn: (row) => breakdown(row).averages.length,
      header: averagesHead,
      enableSorting: false,
      meta: { hideBelow: 'xl' },
      cell: ({ row }) => <AveragesCell breakdown={breakdown(row.original)} />,
    },
    {
      id: 'direction',
      accessorFn: (row) => breakdown(row).direction ?? '',
      header: directionHead,
      enableSorting: false,
      meta: { hideBelow: 'xl' },
      cell: ({ row }) => <DirectionCell breakdown={breakdown(row.original)} />,
    },
  ]
}

/**
 * Signal Zone · Trend signals — each stock's trend checks for one session,
 * A→Z (the API's order): Positive lists what holds, Negative the bearish
 * statements that hold (the opposite of each bullish check the stock misses).
 *
 * MAIN read keyed on date, search and page, so `useQuery` +
 * `keepPreviousData`, not suspense: a new key holds the old rows under
 * RefreshRegion's refresh line. The search box writes `q` to the URL after a
 * pause in typing and follows it on Back; the session and the search each
 * reset to page 1.
 */
function TrendSignalsScreen() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const online = useOnline()
  const query = useTrendSignals(trendQueryOf(search))
  const view = resolveViewState(query, { online })
  const data = query.data
  const isUpdating = query.isPlaceholderData
  const indexSymbols = useIndexSymbols()
  const columns = useMemo(() => columnsFor(search.tab), [search.tab])
  // The search the answer on screen is for, while a new one loads.
  const shownQ = useShownValue(search.q, isUpdating)
  const [text, setText] = useSearchBox(search.q, (q) => {
    void navigate({ search: (prev) => ({ ...prev, q, page: 1 }), replace: true })
  })

  const onSessionChange = (date: string) => {
    void navigate({ search: (prev) => ({ ...prev, date, page: 1 }) })
  }

  const body = (() => {
    if (view === 'failed' || view === 'offline') {
      return isSignalsLocked(query.error) ? (
        <SignalsLocked error={query.error} />
      ) : (
        <EmptyState
          kind={view}
          size="section"
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
          meta={view === 'failed' ? lastUpdatedMeta(query.dataUpdatedAt) : undefined}
          {...(view === 'failed' ? { title: 'Couldn’t load trend signals' } : {})}
        />
      )
    }
    if (data === undefined) {
      return <DataTableSkeleton rows={SIGNAL_DEFAULT_PAGE_SIZE} columns={5} rowHeight="lg" />
    }
    if (data.rows.length === 0) {
      // Every computed session has a trend row for every stock, so an unsearched `[]` is a
      // session nothing was computed for; with a total, it is a page past the end.
      return (
        <RefreshRegion updating={isUpdating}>
          {data.total > 0 ? (
            <SignalsEmpty
              reason="past-end"
              of="the list"
              size="section"
              onFirstPage={() => {
                void navigate({ search: (prev) => ({ ...prev, page: 1 }) })
              }}
            />
          ) : shownQ === undefined ? (
            <SignalsEmpty reason="session" date={data.date} size="section" />
          ) : (
            <EmptyState kind="no-results" size="section" query={shownQ} />
          )}
        </RefreshRegion>
      )
    }
    return (
      <RefreshRegion updating={isUpdating}>
        <DataTable
          rowHeight="lg"
          columns={columns}
          rows={data.rows}
          getRowId={(row) => row.stock.symbol}
          rowLinkOptions={signalRowLink(indexSymbols)}
          washTone="wash"
          nameUnderline={false}
          headWeight="regular"
          caption={
            search.tab === 'positive'
              ? 'Trend conditions each stock meets, A to Z'
              : 'Bearish trend conditions each stock meets, A to Z'
          }
        />
      </RefreshRegion>
    )
  })()

  const showsTable =
    data !== undefined && data.rows.length > 0 && view !== 'failed' && view !== 'offline'

  return (
    <ScreenLayout header={<TitleRow title={TITLE} />} {...signalsRailProps(TITLE)}>
      <SignalsTableFrame
        toolbar={
          <TabBar
            ariaLabel="Trend side"
            idBase={TABS_ID}
            options={TAB_OPTIONS}
            value={search.tab}
            stripClassName="gap-2"
            onChange={(tab) => {
              void navigate({
                search: (prev) => ({ ...prev, tab: tab === 'negative' ? 'negative' : 'positive' }),
              })
            }}
          />
        }
        toolbarClassName="mb-4"
        toolbarEnd={
          <>
            <UpdatingNote when={isUpdating} />
            <SignalsSearchBox value={text} onChange={setText} />
            <SessionDatePicker
              value={search.date ?? data?.date ?? null}
              onChange={onSessionChange}
            />
          </>
        }
        {...(data === undefined || !showsTable
          ? {}
          : {
              paging: {
                page: data.page,
                pageSize: data.pageSize,
                total: data.total,
                onPageChange: (page: number) => {
                  void navigate({ search: (prev) => ({ ...prev, page }) })
                },
                onPageSizeChange: (pageSize) => {
                  void navigate({
                    search: (prev) => ({
                      ...prev,
                      pageSize: toSignalPageSize(pageSize, prev.pageSize),
                      page: 1,
                    }),
                  })
                },
              },
            })}
      >
        <TabPanel idBase={TABS_ID} value={search.tab}>
          {body}
        </TabPanel>
      </SignalsTableFrame>
      {showsTable ? <SignalsFootnote /> : null}
    </ScreenLayout>
  )
}
